export type ChannelMembershipStatus =
  | 'PENDING'
  | 'JOIN_REQUEST_SENT'
  | 'ACTIVE'
  | 'LEFT'
  | 'KICKED'
  | 'BOT_BLOCKED'
  | 'EXPIRED';

export interface ChannelMembershipUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string;
  phone?: string | null;
}

export interface ChannelMembershipChannel {
  id: string;
  title: string;
  telegramId: string;
}

export interface ChannelMembership {
  id: string;
  userId: string;
  channelId: string;
  status: ChannelMembershipStatus;
  inviteLink: string | null;
  joinedAt: string | null;
  leftAt: string | null;
  reason: string | null;
  /** Последняя ошибка при операции (invite/kick) */
  lastError: string | null;
  /** Время последней ошибки */
  lastErrorAt: string | null;
  /** Количество дней до окончания доступа */
  daysRemaining: number | null;
  /** Дата окончания доступа */
  accessEndsAt: string | null;
  /** ID связанного AccessGrant (для редактирования срока доступа) */
  accessGrantId: number | null;
  /** Источник доступа (ORDER, SUBSCRIPTION, etc) */
  accessSourceType: string | null;
  /** Заказ, через который выдан доступ (если sourceType=ORDER) */
  order: {
    id: number;
    total: string | null;
    currency: string | null;
    createdAt: string | null;
  } | null;
  /** Платёж по заказу */
  payment: {
    amount: string | null;
    currency: string | null;
    status: string | null;
    provider: string | null;
    paidAt: string | null;
  } | null;
  /** Купленный тариф (pricing-tier) */
  tier: {
    id: number;
    name: string | null;
  } | null;
  createdAt: string;
  updatedAt: string;
  user: ChannelMembershipUser;
  channel: ChannelMembershipChannel;
}

export interface ChannelMembershipStats {
  total: number;
  active: number;
  left: number;
  blocked: number;
  expired: number;
}

/** Расширенная статистика членств */
export interface ChannelMembershipExtendedStats {
  total: number;
  byStatus: Record<string, number>;
  byChannel: Array<{ channelId: number; channelTitle: string; count: number }>;
  joinedToday: number;
  leftToday: number;
  withErrors: number;
  pending: number;
  joinRequestSent: number;
}

/** Результат проверки статуса в Telegram */
export interface TelegramStatusCheck {
  telegramStatus: string;
  isMember: boolean;
  dbStatus: ChannelMembershipStatus;
  expectedDbStatus: string;
  statusMismatch: boolean;
  syncedAt: string;
}

/** Результат синхронизации */
export interface SyncResult {
  id: number;
  previousStatus: ChannelMembershipStatus;
  newStatus: ChannelMembershipStatus;
  telegramStatus: string;
  synced: boolean;
}

/** Access Grant для связи с подпиской */
export interface AccessGrantInfo {
  id: number;
  userId: number;
  targetType: string;
  targetId: number | null;
  sourceType: string;
  sourceId: number | null;
  startsAt: string | null;
  endsAt: string | null;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Информация о подписке */
export interface SubscriptionInfo {
  id: number;
  userId: number;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** Информация о заказе */
export interface OrderInfo {
  order: {
    id: number;
    userId: number;
    status: string;
    total: string;
    currency: string;
    createdAt: string | null;
    updatedAt: string | null;
  };
  product: {
    id: number;
    type: string;
    price: string;
    channelId: number | null;
  };
}

/** Связанные данные подписки */
export interface SubscriptionData {
  accessGrants: AccessGrantInfo[];
  subscriptions: SubscriptionInfo[];
  orders: OrderInfo[];
}

/** Запись истории изменений */
export interface HistoryEntry {
  id: number;
  action: string;
  actorType: string;
  adminId: number | null;
  userId: number | null;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  createdAt: string | null;
}

/** Результат bulk операции */
export interface BulkOperationResult {
  id: number;
  success: boolean;
  error?: string;
  previousStatus?: string;
  newStatus?: string;
}

/** Сводка bulk операции */
export interface BulkOperationSummary {
  total: number;
  success: number;
  failed: number;
}

/** Ответ bulk операции */
export interface BulkOperationResponse {
  results: BulkOperationResult[];
  summary: BulkOperationSummary;
}

export const MEMBERSHIP_STATUS_LABELS: Record<ChannelMembershipStatus, string> = {
  PENDING: 'Ожидает',
  JOIN_REQUEST_SENT: 'Заявка отправлена',
  ACTIVE: 'В канале',
  LEFT: 'Вышел',
  KICKED: 'Исключён',
  BOT_BLOCKED: 'Бот заблокирован',
  EXPIRED: 'Истекло',
};

export const MEMBERSHIP_STATUS_COLORS: Record<ChannelMembershipStatus, 'default' | 'secondary' | 'destructive' | 'outline' | 'warning'> = {
  PENDING: 'outline',
  JOIN_REQUEST_SENT: 'warning',
  ACTIVE: 'default',
  LEFT: 'secondary',
  KICKED: 'destructive',
  BOT_BLOCKED: 'destructive',
  EXPIRED: 'secondary',
};

/** Описания статусов для UI */
export const MEMBERSHIP_STATUS_DESCRIPTIONS: Record<ChannelMembershipStatus, string> = {
  PENDING: 'Ожидает отправки приглашения',
  JOIN_REQUEST_SENT: 'Ссылка на вступление отправлена пользователю',
  ACTIVE: 'Пользователь находится в канале',
  LEFT: 'Пользователь покинул канал',
  KICKED: 'Пользователь исключён из канала',
  BOT_BLOCKED: 'Пользователь заблокировал бота',
  EXPIRED: 'Доступ к каналу истёк',
};

/** Переводы причин изменения статуса */
export const REASON_LABELS: Record<string, string> = {
  ADMIN_MANUAL_UPDATE: 'Ручное обновление админом',
  ADMIN_MANUAL_INVITE: 'Ручное приглашение админом',
  ADMIN_MANUAL_KICK: 'Ручное исключение админом',
  ADMIN_MANUAL_CREATE: 'Ручное создание админом',
  ADMIN_DELETED: 'Удалено админом',
  ADMIN_RESEND_INVITE: 'Переотправка приглашения',
  SYNCED_FROM_TELEGRAM: 'Синхронизировано из Telegram',
  BULK_SYNC: 'Массовая синхронизация',
  BULK_STATUS_UPDATE: 'Массовое обновление статуса',
  RETRY_INVITE: 'Повторная отправка приглашения',
  RETRY_REVOKE: 'Повторный отзыв доступа',
  SUBSCRIPTION_EXPIRED: 'Подписка истекла',
  SUBSCRIPTION_CANCELLED: 'Подписка отменена',
  ORDER_COMPLETED: 'Заказ выполнен',
  USER_LEFT: 'Пользователь вышел',
  USER_KICKED: 'Пользователь исключён',
  BOT_BLOCKED: 'Бот заблокирован',
  ACCESS_GRANTED: 'Доступ предоставлен',
  ACCESS_REVOKED: 'Доступ отозван',
};

/** Переводы типов источника доступа */
export const ACCESS_SOURCE_TYPE_LABELS: Record<string, string> = {
  ORDER: 'Заказ',
  SUBSCRIPTION: 'Подписка',
  ADMIN: 'Админ',
  PROMOTION: 'Акция',
  MANUAL: 'Вручную',
  SYSTEM: 'Система',
};

/** Переводы типов цели доступа */
export const ACCESS_TARGET_TYPE_LABELS: Record<string, string> = {
  CHANNEL: 'Канал',
  COURSE: 'Курс',
  EVENT: 'Мероприятие',
  PRODUCT: 'Продукт',
};

/** Переводы статусов доступа */
export const ACCESS_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активен',
  EXPIRED: 'Истёк',
  REVOKED: 'Отозван',
  PENDING: 'Ожидает',
};

/** Переводы статусов подписки */
export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Активна',
  CANCELLED: 'Отменена',
  EXPIRED: 'Истекла',
  PENDING: 'Ожидает',
  PAUSED: 'Приостановлена',
};

/** Переводы статусов заказа */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Ожидает',
  PROCESSING: 'В обработке',
  COMPLETED: 'Выполнен',
  CANCELLED: 'Отменён',
  REFUNDED: 'Возвращён',
  FAILED: 'Ошибка',
};

/** Переводы типов продукта */
export const PRODUCT_TYPE_LABELS: Record<string, string> = {
  COURSE: 'Курс',
  EVENT: 'Мероприятие',
  CHANNEL: 'Канал',
  BUNDLE: 'Пакет',
  SUBSCRIPTION: 'Подписка',
};

/** Переводы действий в истории */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  CREATE: 'Создание',
  UPDATE: 'Обновление',
  DELETE: 'Удаление',
  SEND_INVITE: 'Отправка приглашения',
  REVOKE_ACCESS: 'Отзыв доступа',
  SYNC: 'Синхронизация',
  CLEAR_ERROR: 'Очистка ошибки',
  RETRY: 'Повтор операции',
  STATUS_CHANGE: 'Изменение статуса',
};
